// Collections that exist only on this site, not in Shopify. Each one merges the
// products of one or more real Shopify collections under its own handle, so it
// can have its own URL, copy and story layout without a matching collection in
// the Shopify admin.
import { fetchCollection, type CollectionWithProducts, type ShopifyProduct } from "./shopify";

type VirtualCollection = {
  title: string;
  description: string;
  /** Shopify collection handles to merge, in display order. */
  sources: string[];
  /** Keep only products whose title matches, to drop off-topic items from broad sources. */
  titleFilter?: RegExp;
};

export const virtualCollections: Record<string, VirtualCollection> = {
  "glas-lamper": {
    title: "Glas lamper",
    description: "Vintage lamper i glas — Murano og andre glaslamper samlet ét sted.",
    sources: ["murano-lamper", "vintage-lamper"],
    // vintage-lamper also holds cords, marble and fabric-shade lamps.
    titleFilter: /murano|glas|vetri/i,
  },
  "italienske-lamper": {
    title: "Italienske lamper",
    description: "Vintage italienske lamper — Murano og andre lamper fra Italien samlet ét sted.",
    sources: ["murano-lamper", "vintage-lamper"],
    titleFilter: /murano|vetri|ital/i,
  },
  "glas-bordlamper": {
    title: "Glas bordlamper",
    description: "Vintage bordlamper i mundblæst glas — håndplukket hos Farveladeland.",
    sources: ["murano-bordlamper", "vintage-bordlamper"],
    // vintage-bordlamper may also hold lamps with marble feet or fabric shades.
    titleFilter: /murano|glas|vetri/i,
  },
  "vaeglamper-i-glas": {
    title: "Væglamper i glas",
    description:
      "Vintage væglamper i opalglas, farvet og mundblæst glas — håndplukket hos Farveladeland.",
    sources: ["murano-vaeglamper", "vintage-vaeglamper"],
    titleFilter: /murano|glas|vetri/i,
  },
  "glas-pendel": {
    title: "Glas pendel",
    description:
      "Vintage glas pendler — Murano og andre hængelamper i glas, håndplukket hos Farveladeland.",
    sources: ["spisebordslamper", "murano-loftlamper", "vintage-loftlamper", "vintage-lamper"],
    // Hanging lamps only: the sources also hold flush mounts, wall and hallway lamps.
    titleFilter: /^(?!.*(plafond|væglampe|entrelampe)).*(pendel|loftlampe)/i,
  },
};

export async function fetchVirtualCollection(
  handle: string,
  first = 250,
): Promise<CollectionWithProducts | null> {
  const def = virtualCollections[handle];
  if (!def) return null;

  const sources = await Promise.all(def.sources.map((h) => fetchCollection(h, first)));
  // A product can sit in several source collections; keep its first appearance.
  const seen = new Set<string>();
  const edges: ShopifyProduct[] = [];
  for (const col of sources) {
    for (const edge of col?.products.edges ?? []) {
      if (seen.has(edge.node.id)) continue;
      if (def.titleFilter && !def.titleFilter.test(edge.node.title)) continue;
      seen.add(edge.node.id);
      edges.push(edge);
    }
  }

  return {
    id: `virtual:${handle}`,
    handle,
    title: def.title,
    description: def.description,
    image: sources.find((c) => c?.image)?.image ?? null,
    products: { edges },
  };
}
